import React, { useState } from "react";
import { CheckSquare, ClipboardCheck } from "lucide-react";

const VEHICLE_CHECKS = [
  "Fluid Levels with no leaks",
  "Tyres - Roadworthy",
  "Brakes - pressure, no alarms",
  "Lights and Indicators",
  "Mirrors",
  "Windscreens and Wipers",
  "Suspension",
  "Structure and Bodywork",
  "Gauges Operational",
  "Horn Working",
  "No Warning Lights Illuminated",
  "Fluid Level",
  "Seat Belt - In Good Condition",
];

const FIT_TO_START_CHECKS = [
  "The vehicle is currently registered as legally required",
  "I am carrying suitable load restraint equipment, it is free from defects and I am proficient in its use.",
  "The vehicle and other trailing equipment is roadworthy and fit for service including maintenance schedules",
];

const DRIVER_FIT_CHECKS = [
  { id: "licence", label: "I have a correct, current and valid licence to operate this vehicle", options: ["No", "Yes"] },
  { id: "nhvr", label: "My NHVR Work Diary is current and correct (HVNL Regulated Vehicles). Vehicles not covered by the HVNL should mark N/A — all others require a NHVR Work Diary on hand.", options: ["No", "Yes", "N/A"] },
  { id: "fatigue_legal", label: "I am able to complete my current work expectations within legal fatigue and maximum hourly guidelines", options: ["No", "Yes"] },
  { id: "fatigue_fit", label: "I am not fatigued or suffering from any medical condition (that I am aware of) that may affect my ability to complete my allocated tasks", options: ["No", "Yes"] },
  { id: "substances", label: "I have not consumed alcohol and/or drugs (prescription) or otherwise that may impair my ability to work and drive", options: ["No", "Yes"] },
  { id: "fit_work", label: "I am fit to undertake the assigned work", options: ["No", "Yes"] },
];

function buildInitialAnswers() {
  const answers = {};
  VEHICLE_CHECKS.forEach((_, i) => { answers[`v_${i}`] = null; });
  FIT_TO_START_CHECKS.forEach((_, i) => { answers[`f_${i}`] = null; });
  DRIVER_FIT_CHECKS.forEach((c) => { answers[`d_${c.id}`] = null; });
  return answers;
}

// Flatten the keyed answers into an ordered, human-readable list so the admin
// Driver Logs CSV can show exactly which items the driver accepted/declined.
function buildItems(answers) {
  const items = [];
  VEHICLE_CHECKS.forEach((label, i) => items.push({ section: "Vehicle Condition", label, answer: answers[`v_${i}`] }));
  FIT_TO_START_CHECKS.forEach((label, i) => items.push({ section: "Fit to Start Shift", label, answer: answers[`f_${i}`] }));
  DRIVER_FIT_CHECKS.forEach((c) => items.push({ section: "Driver Fit for Service", label: c.label, answer: answers[`d_${c.id}`] }));
  return items;
}

function AnswerToggle({ options, value, onChange }) {
  return (
    <div className="flex gap-1 shrink-0">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className="px-3 py-2 rounded-lg text-xs font-bold transition-all border"
          style={
            value === opt
              ? opt === "Yes" || opt === "N/A"
                ? { background: "#22c55e", color: "#fff", borderColor: "#22c55e" }
                : { background: "#ef4444", color: "#fff", borderColor: "#ef4444" }
              : { background: "transparent", color: "rgba(255,255,255,0.4)", borderColor: "rgba(255,255,255,0.15)" }
          }
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <div className="sticky top-0 z-10 px-5 py-3 rounded-xl mb-2" style={{ background: "rgba(25,230,200,0.08)", borderLeft: "3px solid #19e6c8" }}>
      <p className="text-sm font-bold text-[#19e6c8] uppercase tracking-widest">{children}</p>
    </div>
  );
}

function CheckRow({ label, value, options, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-white/5">
      <p className="text-sm text-white/85 flex-1 leading-snug">{label}</p>
      <AnswerToggle options={options} value={value} onChange={onChange} />
    </div>
  );
}

export default function DriverPrecheck({ driverName, onComplete }) {
  const [answers, setAnswers] = useState(buildInitialAnswers);
  const [comment, setComment] = useState("");

  const setAnswer = (key, val) => setAnswers((a) => ({ ...a, [key]: val }));

  const allAnswered = Object.values(answers).every((v) => v !== null);

  const selectAllYes = () => {
    const all = {};
    VEHICLE_CHECKS.forEach((_, i) => { all[`v_${i}`] = "Yes"; });
    FIT_TO_START_CHECKS.forEach((_, i) => { all[`f_${i}`] = "Yes"; });
    DRIVER_FIT_CHECKS.forEach((c) => { all[`d_${c.id}`] = "Yes"; });
    setAnswers(all);
  };

  const handleDone = () => {
    if (!allAnswered) return;
    onComplete({ answers, comment, items: buildItems(answers) });
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0a0a0f] overflow-y-auto flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#0f0f1a] border-b border-white/10 px-5 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(245,158,11,0.15)" }}>
            <ClipboardCheck size={18} style={{ color: "#f59e0b" }} />
          </span>
          <div>
            <p className="brand-title text-base" style={{ color: "#f59e0b" }}>Start Work Checklist</p>
            <p className="text-xs text-white/40">Welcome, {driverName} — complete all checks to begin</p>
          </div>
        </div>
        <button
          onClick={handleDone}
          disabled={!allAnswered}
          className="px-5 py-2 rounded-xl text-sm font-bold transition-all"
          style={allAnswered
            ? { background: "linear-gradient(90deg,#f59e0b,#facc15)", color: "#1a1a2e" }
            : { background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.25)", cursor: "not-allowed" }}
          data-testid="precheck-done-btn"
        >
          Done
        </button>
      </div>

      <div className="flex-1 max-w-2xl mx-auto w-full px-4 py-6 space-y-6">
        {/* Select All */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={selectAllYes}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold"
            style={{ background: "rgba(34,197,94,0.1)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.2)" }}
            data-testid="select-all-yes"
          >
            <CheckSquare size={15} /> Select All Yes
          </button>
        </div>

        {/* Section 1 — Vehicle Condition */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <div className="px-5 pt-5 pb-2">
            <p className="text-white font-bold text-sm mb-1">Are the following items free from defects and in roadworthy condition?</p>
          </div>
          <div className="px-5 pb-2">
            {VEHICLE_CHECKS.map((label, i) => (
              <CheckRow
                key={i}
                label={label}
                value={answers[`v_${i}`]}
                options={["No", "Yes"]}
                onChange={(v) => setAnswer(`v_${i}`, v)}
              />
            ))}
            <div className="pt-4 pb-2">
              <label className="block text-xs text-white/50 uppercase tracking-widest mb-2">Add Comment If No</label>
              <textarea
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Enter comment for any No answers..."
                className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 resize-none focus:outline-none focus:border-[#19e6c8]"
                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)" }}
                data-testid="precheck-comment"
              />
            </div>
          </div>
        </div>

        {/* Section 2 — Fit to Start Shift */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <SectionHeader>Fit to start shift</SectionHeader>
          <div className="px-5 pb-3">
            {FIT_TO_START_CHECKS.map((label, i) => (
              <CheckRow
                key={i}
                label={label}
                value={answers[`f_${i}`]}
                options={["No", "Yes"]}
                onChange={(v) => setAnswer(`f_${i}`, v)}
              />
            ))}
          </div>
        </div>

        {/* Section 3 — Driver Fit for Service */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <SectionHeader>Driver Fit for Service</SectionHeader>
          <div className="px-5 pb-3">
            {DRIVER_FIT_CHECKS.map((c) => (
              <CheckRow
                key={c.id}
                label={c.label}
                value={answers[`d_${c.id}`]}
                options={c.options}
                onChange={(v) => setAnswer(`d_${c.id}`, v)}
              />
            ))}
          </div>
        </div>

        {/* Bottom done */}
        <div className="flex flex-col items-center gap-2 pt-2 pb-8">
          {!allAnswered && (
            <p className="text-xs text-white/40">Answer all items above to enable Done</p>
          )}
          <button
            onClick={handleDone}
            disabled={!allAnswered}
            className="w-full py-3 rounded-xl font-bold text-base transition-all"
            style={allAnswered
              ? { background: "linear-gradient(90deg,#f59e0b,#facc15)", color: "#1a1a2e" }
              : { background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.25)", cursor: "not-allowed" }}
          >
            Complete Checklist & Access My Runs
          </button>
        </div>
      </div>
    </div>
  );
}
